// ==================== Shared Types & Utilities ====================
// Inlined from @devcourse/shared for standalone deployment compatibility

// Re-export types
export * from './types';

// ==================== User Enums ====================

export const UserRole = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  INSTRUCTOR: 'INSTRUCTOR',
  STUDENT: 'STUDENT',
  AFFILIATE: 'AFFILIATE',
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

// ==================== User DTOs ====================

export interface UserProfileDto {
  id: string;
  email: string;
  name: string;
  avatar?: string | null;
  cpf?: string | null;
  phone?: string | null;
  birthDate?: string | Date | null;
  occupation?: string | null;
  bio?: string | null;
  gender?: string | null;
  education?: string | null;
  experience?: string | null;
  interests?: string | null;
  hobbies?: string | null;
  skills?: string | null;
  addressCountry?: string | null;
  addressZipCode?: string | null;
  addressState?: string | null;
  addressCity?: string | null;
  addressNeighborhood?: string | null;
  addressStreet?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  socialInstagram?: string | null;
  socialLinkedin?: string | null;
  socialGithub?: string | null;
  socialTwitter?: string | null;
  socialWebsite?: string | null;
  termsAcceptedAt?: string | Date | null;
  role: UserRole;
  tier?: 'free' | 'premium' | 'admin';
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  createdAt: string | Date;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// ==================== Common Types ====================

export interface Timestamps {
  createdAt: Date;
  updatedAt: Date;
}

export interface WithId {
  id: string;
}

// ==================== Quiz Types ====================

export const QuizType = {
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  TRUE_FALSE: 'TRUE_FALSE',
  ESSAY: 'ESSAY',
} as const;

export type QuizType = (typeof QuizType)[keyof typeof QuizType];

export interface Quiz extends WithId, Timestamps {
  lessonId?: string | null;
  title: string;
  description?: string | null;
  instructions?: string | null;
  timeLimit?: number | null;
  passingScore: number;
  allowRetries: boolean;
  maxRetries?: number | null;
  showResults: boolean;
  showCorrectAnswer: boolean;
  randomizeOrder: boolean;
  isActive: boolean;
}

export interface QuizWithQuestions extends Quiz {
  questions: QuizQuestion[];
  _count?: {
    attempts: number;
  };
}

export interface CreateQuizDto {
  lessonId?: string;
  title: string;
  description?: string;
  instructions?: string;
  timeLimit?: number;
  passingScore?: number;
  allowRetries?: boolean;
  maxRetries?: number;
  showResults?: boolean;
  showCorrectAnswer?: boolean;
  randomizeOrder?: boolean;
  questions: CreateQuizQuestionDto[];
}

export interface UpdateQuizDto extends Partial<CreateQuizDto> {
  isActive?: boolean;
}

export interface QuizQuestion extends WithId, Timestamps {
  quizId: string;
  type: QuizType;
  question: string;
  explanation?: string | null;
  points: number;
  order: number;
  metadata?: Record<string, unknown>;
}

export interface QuizQuestionWithOptions extends QuizQuestion {
  options: QuizOption[];
}

export interface CreateQuizQuestionDto {
  question: string;
  explanation?: string;
  type?: QuizType;
  points?: number;
  order?: number;
  options: CreateQuizOptionDto[];
}

export interface QuizOption extends WithId {
  questionId: string;
  text: string;
  isCorrect: boolean;
  order: number;
  createdAt: Date;
}

export interface CreateQuizOptionDto {
  text: string;
  isCorrect: boolean;
  order?: number;
}

export interface QuizAttempt extends WithId {
  quizId: string;
  userId: string;
  startedAt: Date;
  completedAt?: Date | null;
  score?: number | null;
  passed: boolean;
  timeSpent?: number | null;
  metadata?: Record<string, unknown>;
}

export interface QuizAttemptWithRelations extends QuizAttempt {
  quiz: Quiz;
  answers?: QuizAnswer[];
}

export interface StartQuizAttemptDto {
  quizId: string;
}

export interface SubmitAnswerDto {
  questionId: string;
  selectedOptions: string[];
  textAnswer?: string;
}

export interface SubmitQuizAttemptDto {
  attemptId: string;
  answers: SubmitAnswerDto[];
}

export interface QuizAnswer extends WithId {
  attemptId: string;
  questionId: string;
  selectedOptions: string[];
  textAnswer?: string | null;
  isCorrect: boolean;
  points: number;
  answeredAt: Date;
}

export interface QuizAnswerWithQuestion extends QuizAnswer {
  question: QuizQuestionWithOptions;
}

export interface QuizStats {
  totalAttempts: number;
  passedAttempts: number;
  failedAttempts: number;
  passRate: number;
  averageScore: number;
}
export * from '../../../packages/shared/src/card-appearance';
export * from '../../../packages/shared/src/member-experience';
export * from '../../../packages/shared/src/formation-banners';
